import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  getWarehouses,
  getStockList,
  createStockTransfer,
  getStockTransfer,
  listStockTransfers,
  scanStockTransfer,
  confirmStockTransfer,
  cancelStockTransfer
} from "../../services/apiService";
import "./StockTransfer.css";

export default function StockTransfer() {
  const [warehouses, setWarehouses] = useState([]);
  const [stockRows, setStockRows] = useState([]);

  const [loadingStocks, setLoadingStocks] = useState(false);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const [scanning, setScanning] = useState(false);

  const [transferList, setTransferList] = useState([]);
  const [transferStatusFilter, setTransferStatusFilter] = useState("PENDING");

  const [transferPage, setTransferPage] = useState(1);
  const [transferPageSize, setTransferPageSize] = useState(10);
  const [transferPagination, setTransferPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasPrev: false,
    hasNext: false,
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  const [fromWarehouseID, setFromWarehouseID] = useState("");
  const [toWarehouseID, setToWarehouseID] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [qty, setQty] = useState(1);
  const [selectedLines, setSelectedLines] = useState([]);

  const [transferView, setTransferView] = useState(null);
  const [scanValue, setScanValue] = useState("");
  const [autoScan, setAutoScan] = useState(true);

  const selectRef = useRef(null);
  const scanRef = useRef(null);
  const itemComboRef = useRef(null);

  const scannerBufferRef = useRef("");
  const scannerTimerRef = useRef(null);
  const scannerBusyRef = useRef(false);
  const lastSubmittedScanRef = useRef({
    code: "",
    at: 0,
  });

  useEffect(() => {
    (async () => {
      try {
        const ws = await getWarehouses();
        setWarehouses(ws || []);
      } catch (err) {
        alert(err?.message || "Failed to load warehouses");
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (scannerTimerRef.current) {
        clearTimeout(scannerTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (itemComboRef.current && !itemComboRef.current.contains(e.target)) {
        setShowItemDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    loadTransfers(transferStatusFilter, transferPage, transferPageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transferStatusFilter, transferPage, transferPageSize]);

  const loadStocks = async () => {
    setLoadingStocks(true);

    try {
      const rows = await getStockList();
      setStockRows(rows || []);
    } catch (err) {
      alert(err?.message || "Failed to load stock list");
    } finally {
      setLoadingStocks(false);
    }
  };

  const loadTransfers = async (
    statusValue = transferStatusFilter,
    pageValue = transferPage,
    pageSizeValue = transferPageSize
  ) => {
    setLoadingTransfers(true);

    try {
      const res = await listStockTransfers({
        status: statusValue,
        page: pageValue,
        pageSize: pageSizeValue,
      });

      setTransferList(res?.rows || []);

      setTransferPagination(
        res?.pagination || {
          page: pageValue,
          pageSize: pageSizeValue,
          total: 0,
          totalPages: 1,
          hasPrev: false,
          hasNext: false,
        }
      );
    } catch (err) {
      alert(err?.message || "Failed to load transfer list");
      setTransferList([]);
      setTransferPagination({
        page: 1,
        pageSize: pageSizeValue,
        total: 0,
        totalPages: 1,
        hasPrev: false,
        hasNext: false,
      });
    } finally {
      setLoadingTransfers(false);
    }
  };

  const resetCreateForm = () => {
    setFromWarehouseID("");
    setToWarehouseID("");
    setSelectedKey("");
    setItemSearch("");
    setShowItemDropdown(false);
    setQty(1);
    setSelectedLines([]);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    resetCreateForm();
  };

  const closeTransferModal = () => {
    if (scannerTimerRef.current) {
      clearTimeout(scannerTimerRef.current);
    }

    scannerBufferRef.current = "";
    scannerBusyRef.current = false;
    lastSubmittedScanRef.current = {
      code: "",
      at: 0,
    };

    setShowTransferModal(false);
    setTransferView(null);
    setScanValue("");
    setScanning(false);
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    resetCreateForm();
    await loadStocks();
    setTimeout(() => selectRef.current?.focus(), 0);
  };

  const handleFromWarehouseChange = async (val) => {
    setFromWarehouseID(val);
    setSelectedKey("");
    setItemSearch("");
    setShowItemDropdown(false);
    setQty(1);
    setSelectedLines([]);

    if (!stockRows.length) {
      await loadStocks();
    }

    setTimeout(() => selectRef.current?.focus(), 0);
  };

  const options = useMemo(() => {
    const wid = String(fromWarehouseID || "");
    if (!wid) return [];

    return (stockRows || [])
      .filter((r) => String(r.WarehouseID) === wid)
      .filter((r) => Number(r.TotalPieces || 0) > 0)
      .map((r) => {
        const key = `${r.ItemID}|${r.LotNumber}|${r.ExpiryDate}|${r.WarehouseID}`;
        const availableQty = Number(r.TotalPieces || 0);
        const label = `${r.ItemName} (${r.CatalogNo}) | Lot:${r.LotNumber} | Exp:${r.ExpiryDate} | Avail:${availableQty}`;

        return {
          key,
          label,
          data: {
            key,
            itemId: r.ItemID,
            itemName: r.ItemName,
            catalogNo: r.CatalogNo,
            lotNumber: r.LotNumber,
            expiryDate: r.ExpiryDate,
            fromWarehouseID: r.WarehouseID,
            fromWarehouseName: r.WarehouseName,
            availableQty
          }
        };
      });
  }, [stockRows, fromWarehouseID]);

  const filteredOptions = useMemo(() => {
    const q = String(itemSearch || "").trim().toLowerCase();

    if (!q) return options;

    return options.filter((o) => {
      const d = o.data || {};

      return [
        o.label,
        d.itemName,
        d.catalogNo,
        d.lotNumber,
        d.expiryDate,
        d.availableQty
      ]
        .join(" ")
        .toLowerCase()
        .includes(q);
    });
  }, [options, itemSearch]);

  const selected = useMemo(() => {
    return options.find((o) => o.key === selectedKey)?.data || null;
  }, [options, selectedKey]);

  const status = String(transferView?.header?.Status || "").toUpperCase();
  const canScan = status === "PENDING";
  const transferId = transferView?.header?.TransferID;

  const totalRequired = useMemo(() => {
    return (transferView?.lines || []).reduce(
      (sum, l) => sum + Number(l.Qty || 0),
      0
    );
  }, [transferView]);

  const totalMoved = useMemo(() => {
    return (transferView?.lines || []).reduce(
      (sum, l) => sum + Number(l.MovedQty || 0),
      0
    );
  }, [transferView]);

  const totalRemaining = Math.max(0, totalRequired - totalMoved);
  const isComplete = totalRemaining === 0 && totalRequired > 0;

  const transferStatusTitle =
    transferStatusFilter === "ALL"
      ? "All Transfers"
      : transferStatusFilter === "CONFIRMED"
        ? "Completed Transfers"
        : transferStatusFilter === "CANCELLED"
          ? "Cancelled Transfers"
          : "Pending Transfers";

  const getStatusLabel = (value) => {
    const s = String(value || "").toUpperCase();

    if (s === "CONFIRMED") return "COMPLETED";
    if (s === "CANCELLED") return "CANCELLED";
    if (s === "PENDING") return "PENDING";

    return s || "UNKNOWN";
  };

  const getStatusClass = (value) => {
    const s = String(value || "").toUpperCase();

    if (s === "CONFIRMED") return "confirmed";
    if (s === "CANCELLED") return "cancelled";
    if (s === "PENDING") return "pending";

    return "pending";
  };

  const normalizeBarcode = (value) => {
    return String(value || "")
      .replace(/[\r\n\t]/g, "")
      .trim()
      .toUpperCase();
  };

  const handleAutoScanToggle = (checked) => {
    setAutoScan(checked);

    if (scannerTimerRef.current) {
      clearTimeout(scannerTimerRef.current);
    }

    scannerBufferRef.current = "";
    setScanValue("");

    if (!checked) {
      setTimeout(() => scanRef.current?.focus(), 0);
    }
  };

  const submitBarcodeScan = useCallback(
    async (rawBarcode) => {
      const barcode = normalizeBarcode(rawBarcode);

      if (!barcode) return;

      if (!transferId) {
        alert("Open a transfer first");
        return;
      }

      if (!canScan) {
        alert("Only PENDING transfer can be scanned");
        return;
      }

      if (scannerBusyRef.current) return;

      const now = Date.now();
      const lastScan = lastSubmittedScanRef.current;

      if (lastScan.code === barcode && now - lastScan.at < 1500) {
        scannerBufferRef.current = "";
        setScanValue("");
        return;
      }

      scannerBusyRef.current = true;
      setScanning(true);
      setScanValue(barcode);

      try {
        const res = await scanStockTransfer(transferId, barcode);

        if (res?.bundle) {
          setTransferView(res.bundle);
        }

        lastSubmittedScanRef.current = {
          code: barcode,
          at: Date.now(),
        };

        scannerBufferRef.current = "";
        setScanValue("");
      } catch (err) {
        scannerBufferRef.current = "";
        setScanValue("");
        alert(err?.message || "Scan failed");
      } finally {
        scannerBusyRef.current = false;
        setScanning(false);

        if (!autoScan) {
          setTimeout(() => scanRef.current?.focus(), 0);
        }
      }
    },
    [transferId, canScan, autoScan]
  );

  useEffect(() => {
    if (!showTransferModal || !canScan || !autoScan) return;

    const flushScannerBuffer = () => {
      const code = normalizeBarcode(scannerBufferRef.current);

      scannerBufferRef.current = "";
      setScanValue("");

      if (code) {
        submitBarcodeScan(code);
      }
    };

    const handleScannerKeyDown = (e) => {
      if (e.ctrlKey || e.altKey || e.metaKey) return;

      if (e.key === "Enter") {
        e.preventDefault();

        if (scannerTimerRef.current) {
          clearTimeout(scannerTimerRef.current);
        }

        flushScannerBuffer();
        return;
      }

      if (e.key.length !== 1) return;

      e.preventDefault();

      scannerBufferRef.current += e.key;
      setScanValue(scannerBufferRef.current);

      if (scannerTimerRef.current) {
        clearTimeout(scannerTimerRef.current);
      }

      scannerTimerRef.current = setTimeout(() => {
        flushScannerBuffer();
      }, 180);
    };

    document.addEventListener("keydown", handleScannerKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleScannerKeyDown, true);

      if (scannerTimerRef.current) {
        clearTimeout(scannerTimerRef.current);
      }

      scannerBufferRef.current = "";
    };
  }, [showTransferModal, canScan, autoScan, submitBarcodeScan]);

  const addLine = () => {
    if (!fromWarehouseID) return alert("Please select FROM warehouse first");
    if (!selected) return alert("Please select a stock item");

    const q = Number(qty);

    if (!q || q <= 0) return alert("Qty must be at least 1");

    if (q > Number(selected.availableQty)) {
      return alert("Qty exceeds available stock");
    }

    setSelectedLines((prev) => {
      const exists = prev.find((x) => x.key === selected.key);

      if (!exists) {
        return [...prev, { ...selected, qty: q }];
      }

      const newQty = Number(exists.qty) + q;

      if (newQty > Number(selected.availableQty)) {
        alert("Total qty exceeds available stock");
        return prev;
      }

      return prev.map((x) =>
        x.key === selected.key ? { ...x, qty: newQty } : x
      );
    });

    setSelectedKey("");
    setItemSearch("");
    setShowItemDropdown(false);
    setQty(1);
    setTimeout(() => selectRef.current?.focus(), 0);
  };

  const removeLine = (key) => {
    setSelectedLines((prev) => prev.filter((x) => x.key !== key));
  };

  const handleCreateTransfer = async () => {
    if (!fromWarehouseID) return alert("Please select FROM warehouse");
    if (!toWarehouseID) return alert("Please select TO warehouse");

    if (String(fromWarehouseID) === String(toWarehouseID)) {
      return alert("FROM and TO cannot be same");
    }

    if (selectedLines.length === 0) return alert("No items selected");

    setCreating(true);

    try {
      const payload = {
        fromWarehouseID,
        toWarehouseID,
        lines: selectedLines.map((l) => ({
          itemId: l.itemId,
          lotNumber: l.lotNumber,
          expiryDate: l.expiryDate,
          qty: l.qty
        }))
      };

      const res = await createStockTransfer(payload);

      setTransferStatusFilter("PENDING");
      setTransferPage(1);
      await loadTransfers("PENDING", 1, transferPageSize);

      const view = await getStockTransfer(Number(res.transferId));
      setTransferView(view);
      setScanValue("");

      setShowCreateModal(false);
      resetCreateForm();
      setShowTransferModal(true);
    } catch (err) {
      alert(err?.message || "Create transfer failed");
    } finally {
      setCreating(false);
    }
  };

  const handleOpenTransfer = async (id) => {
    const selectedTransferId = Number(id || 0);
    if (!selectedTransferId) return alert("Invalid transfer");

    setOpeningId(selectedTransferId);

    try {
      const res = await getStockTransfer(selectedTransferId);
      setTransferView(res);
      setScanValue("");
      setShowTransferModal(true);
    } catch (err) {
      alert(err?.message || "Failed to open transfer");
      setTransferView(null);
      setShowTransferModal(false);
    } finally {
      setOpeningId(null);
    }
  };

  const handleConfirm = async () => {
    if (!transferId) return alert("No transfer opened");
    if (!canScan) return alert("Only PENDING transfers can be confirmed");

    if (!isComplete) {
      return alert("Not complete yet. Scan remaining items first.");
    }

    if (!window.confirm("Confirm this transfer? This will MOVE the scanned stocks.")) {
      return;
    }

    try {
      await confirmStockTransfer(transferId);
      alert("✅ Transfer confirmed");

      setTransferStatusFilter("CONFIRMED");
      setTransferPage(1);
      await loadTransfers("CONFIRMED", 1, transferPageSize);
      await loadStocks();

      closeTransferModal();
    } catch (err) {
      alert(err?.message || "Confirm failed");
    }
  };

  const handleCancel = async () => {
    if (!transferId) return alert("No transfer opened");
    if (!canScan) return alert("Only PENDING transfers can be cancelled");

    const reason = window.prompt("Cancel reason:", "");

    if (reason === null) return;

    try {
      await cancelStockTransfer(transferId, reason);
      alert("Transfer cancelled");

      setTransferStatusFilter("CANCELLED");
      setTransferPage(1);
      await loadTransfers("CANCELLED", 1, transferPageSize);

      closeTransferModal();
    } catch (err) {
      alert(err?.message || "Cancel failed");
    }
  };

  const handlePrint = () => {
    if (!transferView?.header) return alert("Open a transfer first");
    window.print();
  };

  const pHeader = transferView?.header || null;
  const pLines = transferView?.lines || [];

  return (
    <div className="stock-transfer-page">
      <div className="st-toolbar">
        <div>
          <div className="st-eyebrow">Inventory Operations</div>
          <h2>Stock Transfer</h2>
          <p>Manage stock transfers between warehouses.</p>
        </div>

        <button className="st-btn-primary" onClick={openCreateModal}>
          + New Transfer
        </button>
      </div>

      <div className="st-card">
        <div className="st-card-head">
          <div>
            <h3>{transferStatusTitle}</h3>
            <p className="st-head-copy">
              View pending, completed, cancelled, or all stock transfers.
            </p>
          </div>

          <div className="st-list-toolbar">
            <select
              className="st-select"
              value={transferStatusFilter}
              onChange={(e) => {
                setTransferStatusFilter(e.target.value);
                setTransferPage(1);
              }}
            >
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Completed</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="ALL">All</option>
            </select>

            <span className="st-mini-pill">
              {transferPagination.total} transfer(s)
            </span>

            <button
              className="st-btn"
              onClick={() =>
                loadTransfers(
                  transferStatusFilter,
                  transferPage,
                  transferPageSize
                )
              }
              disabled={loadingTransfers}
            >
              {loadingTransfers ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {transferList.length > 0 ? (
          <div className="st-table">
            <div className="st-thead st-thead-list">
              <div>Transfer No</div>
              <div>From</div>
              <div>To</div>
              <div>Created</div>
              <div>Status</div>
              <div>Action</div>
            </div>

            {transferList.map((t) => (
              <div className="st-trow st-trow-list" key={t.TransferID}>
                <div>
                  <b>{t.TransferNo}</b>
                </div>

                <div>{t.FromWarehouseName}</div>
                <div>{t.ToWarehouseName}</div>
                <div>{t.CreatedAt}</div>

                <div>
                  <span className={`st-status ${getStatusClass(t.Status)}`}>
                    {getStatusLabel(t.Status)}
                  </span>
                </div>

                <div>
                  <button
                    className="st-btn-primary"
                    onClick={() => handleOpenTransfer(t.TransferID)}
                    disabled={openingId === t.TransferID}
                  >
                    {openingId === t.TransferID ? "Opening..." : "Open"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          !loadingTransfers && (
            <div className="st-empty-state">No transfers found.</div>
          )
        )}

        <div className="st-pagination">
          <div className="st-pagination-info">
            Showing page <b>{transferPagination.page}</b> of{" "}
            <b>{transferPagination.totalPages}</b> · Total:{" "}
            <b>{transferPagination.total}</b>
          </div>

          <div className="st-pagination-controls">
            <select
              className="st-page-size"
              value={transferPageSize}
              onChange={(e) => {
                setTransferPageSize(Number(e.target.value));
                setTransferPage(1);
              }}
            >
              <option value={5}>5 / page</option>
              <option value={10}>10 / page</option>
              <option value={20}>20 / page</option>
              <option value={50}>50 / page</option>
            </select>

            <button
              className="st-btn"
              disabled={!transferPagination.hasPrev || loadingTransfers}
              onClick={() => setTransferPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>

            <button
              className="st-btn"
              disabled={!transferPagination.hasNext || loadingTransfers}
              onClick={() =>
                setTransferPage((p) =>
                  Math.min(transferPagination.totalPages || 1, p + 1)
                )
              }
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="st-modal-overlay" onClick={closeCreateModal}>
          <div
            className="st-modal st-modal-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="st-modal-header">
              <div>
                <h3>Create Stock Transfer</h3>
                <p>Plan the transfer just like your SO modal flow.</p>
              </div>

              <button className="st-icon-close" onClick={closeCreateModal}>
                ✕
              </button>
            </div>

            <div className="st-form-grid">
              <div className="st-field">
                <label className="st-label">From Warehouse</label>

                <select
                  className="st-select"
                  value={fromWarehouseID}
                  onChange={(e) => handleFromWarehouseChange(e.target.value)}
                >
                  <option value="">-- Select Warehouse --</option>

                  {warehouses.map((w) => (
                    <option key={w.warehouseId} value={w.warehouseId}>
                      {w.warehouseName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="st-field">
                <label className="st-label">To Warehouse</label>

                <select
                  className="st-select"
                  value={toWarehouseID}
                  onChange={(e) => setToWarehouseID(e.target.value)}
                >
                  <option value="">-- Select Warehouse --</option>

                  {warehouses
                    .filter((w) => String(w.warehouseId) !== String(fromWarehouseID))
                    .map((w) => (
                      <option key={w.warehouseId} value={w.warehouseId}>
                        {w.warehouseName}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="st-card-lite">
              <div className="st-card-head">
                <div>
                  <h4>Add Transfer Items</h4>
                  <p className="st-head-copy">
                    Choose stock from the selected source warehouse.
                  </p>
                </div>
              </div>

              {loadingStocks ? (
                <div className="st-muted">Loading available stocks…</div>
              ) : (
                <>
                  <label className="st-label">Available Stocks</label>

                  <div className="st-combo" ref={itemComboRef}>
                    <input
                      ref={selectRef}
                      className="st-input"
                      value={itemSearch}
                      disabled={!fromWarehouseID}
                      placeholder={
                        fromWarehouseID
                          ? "Type item name, catalog no, lot, or expiry..."
                          : "Select FROM warehouse first"
                      }
                      onFocus={() => {
                        if (fromWarehouseID) setShowItemDropdown(true);
                      }}
                      onChange={(e) => {
                        setItemSearch(e.target.value);
                        setSelectedKey("");
                        if (fromWarehouseID) setShowItemDropdown(true);
                      }}
                    />

                    {showItemDropdown && fromWarehouseID && (
                      <div className="st-combo-menu">
                        {filteredOptions.length > 0 ? (
                          filteredOptions.map((o) => (
                            <button
                              type="button"
                              key={o.key}
                              className={`st-combo-option ${
                                selectedKey === o.key ? "selected" : ""
                              }`}
                              onClick={() => {
                                setSelectedKey(o.key);
                                setItemSearch(o.label);
                                setShowItemDropdown(false);
                              }}
                            >
                              <div className="st-combo-main">
                                <b>{o.data.itemName}</b>
                                <span>{o.data.catalogNo}</span>
                              </div>

                              <div className="st-combo-sub">
                                Lot: <b>{o.data.lotNumber}</b> · Exp:{" "}
                                <b>{o.data.expiryDate}</b> · Avail:{" "}
                                <b>{o.data.availableQty}</b>
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="st-combo-empty">
                            No stock found for "{itemSearch}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="st-row">
                    <div className="st-field">
                      <label className="st-label">Qty</label>

                      <input
                        className="st-input"
                        type="number"
                        min="1"
                        value={qty}
                        onChange={(e) => setQty(e.target.value)}
                      />

                      {selected && (
                        <div className="st-help">
                          Max available: <b>{selected.availableQty}</b>
                        </div>
                      )}
                    </div>

                    <button className="st-btn-primary" onClick={addLine}>
                      + Add Line
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="st-section-top">
              <div className="st-section-title-row">
                <b>Selected Items</b>
                <span className="st-mini-pill">
                  {selectedLines.length} item(s)
                </span>
              </div>

              {selectedLines.length === 0 ? (
                <div className="st-empty-state">No items selected yet.</div>
              ) : (
                <div className="st-table">
                  <div className="st-thead">
                    <div>Item</div>
                    <div>Lot</div>
                    <div>Exp</div>
                    <div>Avail</div>
                    <div>Qty</div>
                    <div>Action</div>
                  </div>

                  {selectedLines.map((l) => (
                    <div className="st-trow" key={l.key}>
                      <div>
                        <b>{l.itemName}</b>
                        <div className="st-sub">{l.catalogNo}</div>
                      </div>

                      <div>{l.lotNumber}</div>
                      <div>{l.expiryDate}</div>
                      <div>{l.availableQty}</div>
                      <div className="st-qty">{l.qty}</div>

                      <div>
                        <button
                          className="st-btn-danger"
                          onClick={() => removeLine(l.key)}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="st-actions">
              <button className="st-btn" onClick={closeCreateModal}>
                Cancel
              </button>

              <button
                className="st-btn-primary"
                disabled={creating}
                onClick={handleCreateTransfer}
              >
                {creating ? "Creating..." : "Create Transfer"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTransferModal && transferView?.header && (
        <div className="st-modal-overlay" onClick={closeTransferModal}>
          <div className="st-modal" onClick={(e) => e.stopPropagation()}>
            <div className="st-modal-header">
              <div>
                <h3>{transferView.header.TransferNo}</h3>
                <p>Scan, review, and confirm this transfer.</p>
              </div>

              <button className="st-icon-close" onClick={closeTransferModal}>
                ✕
              </button>
            </div>

            <div className="st-transfer-title">
              <div>
                <b>{transferView.header.TransferNo}</b>
                <div className="st-sub">Transfer details and progress monitor</div>
              </div>

              <span className={`st-status ${getStatusClass(transferView.header.Status)}`}>
                {getStatusLabel(transferView.header.Status)}
              </span>
            </div>

            <div className="st-transfer-meta">
              <div>
                <b>From:</b> {transferView.header.FromWarehouseName}
              </div>

              <div>
                <b>To:</b> {transferView.header.ToWarehouseName}
              </div>

              <div>
                <b>Created:</b> {transferView.header.CreatedAt} by{" "}
                {transferView.header.CreatedBy}
              </div>

              {String(transferView.header.Status || "").toUpperCase() === "CONFIRMED" && (
                <div>
                  <b>Confirmed:</b> {transferView.header.ConfirmedAt || "-"} by{" "}
                  {transferView.header.ConfirmedBy || "-"}
                </div>
              )}

              {String(transferView.header.Status || "").toUpperCase() === "CANCELLED" && (
                <>
                  <div>
                    <b>Cancelled:</b> {transferView.header.CancelledAt || "-"} by{" "}
                    {transferView.header.CancelledBy || "-"}
                  </div>

                  <div>
                    <b>Reason:</b> {transferView.header.CancelReason || "-"}
                  </div>
                </>
              )}
            </div>

            <div className="st-progress">
              <div className="st-progress-pill">
                <span>Required</span>
                <strong>{totalRequired}</strong>
              </div>

              <div className="st-progress-pill">
                <span>Moved</span>
                <strong>{totalMoved}</strong>
              </div>

              <div className={`st-progress-pill ${isComplete ? "ok" : "warn"}`}>
                <span>Remaining</span>
                <strong>{totalRemaining}</strong>
              </div>
            </div>

            <div className="st-scan">
              <div className="st-section-title-row">
                <label className="st-label">
                  {autoScan ? "Scanner" : "Manual Barcode Input"}
                </label>

                <label className="st-mini-pill" style={{ cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={autoScan}
                    disabled={!canScan || scanning}
                    onChange={(e) => handleAutoScanToggle(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Auto Scan
                </label>
              </div>

              <div className="st-row">
                <input
                  ref={scanRef}
                  className="st-input"
                  value={scanValue}
                  readOnly={autoScan}
                  tabIndex={autoScan ? -1 : 0}
                  placeholder={
                    autoScan
                      ? "Scanner ready — just scan, no need to click"
                      : "Type barcode manually then press Enter"
                  }
                  disabled={!canScan || scanning}
                  onChange={(e) => {
                    if (!autoScan) {
                      setScanValue(e.target.value.toUpperCase());
                    }
                  }}
                  onKeyDown={(e) => {
                    if (!autoScan && e.key === "Enter") {
                      e.preventDefault();
                      submitBarcodeScan(scanValue);
                    }
                  }}
                />

                <button
                  className="st-btn-primary"
                  type="button"
                  onClick={() => submitBarcodeScan(scanValue)}
                  disabled={!canScan || scanning || !normalizeBarcode(scanValue)}
                >
                  {scanning
                    ? "Scanning..."
                    : autoScan
                      ? "Submit"
                      : "Submit Manual Barcode"}
                </button>
              </div>

              {canScan && autoScan && (
                <div className="st-muted">
                  Scanner is active. Just scan while this modal is open. No need
                  to click the barcode field.
                </div>
              )}

              {canScan && !autoScan && (
                <div className="st-muted">
                  Manual input is active. Type the barcode, then press Enter or
                  click Submit Manual Barcode.
                </div>
              )}

              {!canScan && (
                <div className="st-muted">
                  Scanning is only allowed while status is PENDING.
                </div>
              )}
            </div>

            <div className="st-section-top">
              <div className="st-section-title-row">
                <b>Transfer Lines</b>
                <span className="st-mini-pill">
                  {(transferView.lines || []).length} line(s)
                </span>
              </div>

              <div className="st-table">
                <div className="st-thead">
                  <div>Item</div>
                  <div>Lot</div>
                  <div>Exp</div>
                  <div>Req</div>
                  <div>Moved</div>
                  <div>Remain</div>
                </div>

                {(transferView.lines || []).map((l) => {
                  const remain = Number(l.RemainingQty || 0);

                  return (
                    <div className="st-trow" key={l.LineID}>
                      <div>
                        <b>{l.ItemName}</b>
                        <div className="st-sub">{l.CatalogNo}</div>
                      </div>

                      <div>{l.LotNumber}</div>
                      <div>{l.ExpiryDate}</div>
                      <div className="st-qty">{l.Qty}</div>
                      <div className="st-qty">{l.MovedQty}</div>

                      <div className={`st-qty ${remain === 0 ? "st-ok" : "st-warn"}`}>
                        {remain}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="st-actions">
              <button
                className="st-btn-primary"
                onClick={handleConfirm}
                disabled={!canScan || !isComplete}
              >
                Confirm Transfer
              </button>

              <button
                className="st-btn-danger"
                onClick={handleCancel}
                disabled={!canScan}
              >
                Cancel Transfer
              </button>

              <button
                className="st-btn"
                onClick={handlePrint}
                disabled={!transferView?.header}
              >
                Print Transfer
              </button>

              <button className="st-btn" onClick={closeTransferModal}>
                Close
              </button>
            </div>

            {!isComplete && canScan && (
              <div className="st-note">
                Keep scanning until <b>Remaining</b> becomes <b>0</b>, then you can
                confirm.
              </div>
            )}
          </div>
        </div>
      )}

      {pHeader && (
        <div id="stock-transfer-print" className="stp-wrap">
          <div className="stp-top-simple">
            <div className="stp-left">
              <div className="stp-title-simple">STOCK TRANSFER</div>

              <div className="stp-trfno">
                TRF No: <b>{pHeader.TransferNo}</b>
              </div>
            </div>

            <div className="stp-fromto">
              <div>
                <span className="stp-mini-label">FROM:</span>{" "}
                <b>{pHeader.FromWarehouseName}</b>
              </div>

              <div>
                <span className="stp-mini-label">TO:</span>{" "}
                <b>{pHeader.ToWarehouseName}</b>
              </div>
            </div>
          </div>

          <table className="stp-table">
            <thead>
              <tr>
                <th style={{ width: "45%" }}>Item</th>
                <th style={{ width: "18%" }}>Catalog</th>
                <th style={{ width: "14%" }}>Lot</th>
                <th style={{ width: "13%" }}>Expiry</th>
                <th style={{ width: "10%" }}>Qty</th>
              </tr>
            </thead>

            <tbody>
              {pLines.map((l) => (
                <tr key={l.LineID}>
                  <td>
                    <b>{l.ItemName}</b>
                  </td>
                  <td>{l.CatalogNo}</td>
                  <td>{l.LotNumber}</td>
                  <td>{l.ExpiryDate}</td>
                  <td className="stp-num">{l.Qty}</td>
                </tr>
              ))}

              {pLines.length === 0 && (
                <tr>
                  <td colSpan={5} className="stp-empty">
                    No items.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}